import { useState, useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';
import { useContractWrite, usePrepareContractWrite, useAccount, useWaitForTransaction, useContractRead, usePublicClient, useWalletClient } from 'wagmi';
import { ADDRESSES, CONFIG } from '../config';
import { Abi } from 'viem';
import NT_ABI from '../../abis/NT.json';
import { createSimpleEntityPage } from '../utils/ipfsUpload';
import { uploadEntityPageToBackend, testBackendConnection, uploadDirectoryToBackend } from '../utils/backendUpload';
import { generateEntityPageFiles } from '../utils/uiTemplates';

interface CreateEntityFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

type EntityType = 'Bank' | 'Store' | 'Hospital' | 'Bond Management';

const ENTITY_TYPES: EntityType[] = ['Bank', 'Store', 'Hospital', 'Bond Management'];

export function CreateEntityForm({ onCancel, onSuccess }: CreateEntityFormProps) {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedType, setSelectedType] = useState<EntityType>('Bank');
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [entityToken, setEntityToken] = useState<string>(ADDRESSES.TRINITY_TOKEN);

  // Backend upload state
  const [isUploadingToIPFS, setIsUploadingToIPFS] = useState(false);
  const [ipfsUploadProgress, setIpfsUploadProgress] = useState<string>('');
  const [ipfsCID, setIpfsCID] = useState<string>('');
  const [isBackendReady, setIsBackendReady] = useState<boolean | null>(null);

  // Token launch form state
  const [tokenName, setTokenName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [initialMint, setInitialMint] = useState('1000000');
  const [parentToken, setParentToken] = useState<`0x${string}`>(ADDRESSES.FED);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenTxHash, setTokenTxHash] = useState<`0x${string}` | undefined>();
  const [approvalTxHash, setApprovalTxHash] = useState<`0x${string}` | undefined>();

  // Add approval state
  const [needsApproval, setNeedsApproval] = useState(false);
  const [approvalAmount, setApprovalAmount] = useState<bigint>(0n);

  // Initialize backend connection check
  useEffect(() => {
    const testConnection = async () => {
      try {
        const isConnected = await testBackendConnection();
        setIsBackendReady(isConnected);
        console.log('Backend connection test result:', isConnected);
      } catch (error) {
        console.error('Backend connection test failed:', error);
        setIsBackendReady(false);
      }
    };

    testConnection();
  }, []);

  // Check allowance for parent token
  const { data: allowance = 0n } = useContractRead({
    address: parentToken,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'allowance',
    args: [address, ADDRESSES.NT],
    enabled: !!address && !!parentToken && showTokenModal,
    watch: true,
  });

  // Check if approval is needed
  useEffect(() => {
    if (showTokenModal && initialMint) {
      const requiredAmount = BigInt(initialMint) * 10n ** 18n;
      setApprovalAmount(requiredAmount);
      setNeedsApproval((allowance as bigint) < requiredAmount);
    }
  }, [showTokenModal, initialMint, allowance]);

  // Prepare approval transaction
  const { config: approvalConfig } = usePrepareContractWrite({
    address: parentToken,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'approve',
    args: [ADDRESSES.NT, approvalAmount],
    enabled: needsApproval && !!address && !!parentToken && approvalAmount > 0n,
  });

  const { write: approve, isLoading: isApproving } = useContractWrite({
    ...approvalConfig,
    onSuccess: (data) => {
      setApprovalTxHash(data.hash);
      setTokenError(null);
    },
    onError: (error) => {
      setTokenError(`Approval failed: ${error.message}`);
    },
  });

  // Watch approval transaction
  const { isLoading: isApprovalConfirming } = useWaitForTransaction({
    hash: approvalTxHash,
    onSuccess: () => {
      setNeedsApproval(false);
      setApprovalTxHash(undefined);
    },
    onError: () => {
      setTokenError('Approval transaction failed');
      setApprovalTxHash(undefined);
    },
  });

  // Log state changes
  useEffect(() => {
    console.log('Form State:', {
      name,
      description,
      selectedType,
      address,
      isEnabled: Boolean(name && description && selectedType && address),
      masterAddress: ADDRESSES.TRINITY_MASTER,
      ipfsCID,
      isBackendReady: isBackendReady ? 'Ready' : 'Not Ready'
    });
    
    // Debug upload button state
    console.log('Upload Button State:', {
      isUploadingToIPFS,
      hasBackendReady: isBackendReady !== null,
      hasName: !!name,
      hasDescription: !!description,
      buttonDisabled: isUploadingToIPFS || isBackendReady === false || !name || !description,
      disabledReasons: {
        isUploadingToIPFS,
        noBackendReady: isBackendReady === false,
        noName: !name,
        noDescription: !description
      }
    });
  }, [name, description, selectedType, address, ipfsCID, isBackendReady, isUploadingToIPFS]);

  // Prepare contract write for deploying entity
  const { config: deployEntityConfig, error: prepareError } = usePrepareContractWrite({
    address: ADDRESSES.TRINITY_MASTER,
    abi: CONFIG.TRINITY_ABI.abi as Abi,
    functionName: 'createEntity',
    args: [
      name || '',  // Ensure non-null
      description || '',  // Ensure non-null
      selectedType || 'Bank',  // Ensure non-null
      entityToken // Use entityToken instead of ADDRESSES.TRINITY_TOKEN
    ],
    enabled: !!name && !!description && !!selectedType && !!address && !!entityToken,
  });

  // Log prepare errors
  useEffect(() => {
    if (prepareError) {
      console.log('Prepare Error:', {
        message: prepareError.message,
        name: prepareError.name,
        details: prepareError
      });
    }
  }, [prepareError]);

  const { write: deployEntity, isLoading: isSubmitting } = useContractWrite({
    ...deployEntityConfig,
    onSuccess: (data) => {
      console.log('Write Success:', data);
      setTxHash(data.hash);
      setError(null);
    },
    onError: (error) => {
      console.error('Write Error:', error);
      setError(error.message);
    },
  });

  // Monitor transaction status
  const { isLoading: isConfirming } = useWaitForTransaction({
    hash: txHash,
    onSuccess: async (receipt) => {
      console.log('Transaction confirmed');
      console.log('Transaction receipt:', receipt);
      
      try {
        // Step 1: Extract entity address from transaction receipt
        setIpfsUploadProgress('Entity created! Extracting entity address...');
        console.log('Transaction receipt:', receipt);
        console.log('Transaction logs:', receipt.logs);
        
        // Look for the EntityAdded event in the logs
        let entityAddress: string | null = null;
        if (receipt.logs) {
          for (const log of receipt.logs) {
            try {
              console.log('Processing log:', log);
              // Try to decode the log to find the EntityAdded event
              // The EntityAdded event has signature: EntityAdded(address indexed entity)
              if (log.topics && log.topics.length > 0) {
                // Calculate the EntityAdded event signature
                // keccak256("EntityAdded(address)")
                const entityAddedTopic = '0x4f51faf6c4561ff95f067657e43439f0f856d97c04d9f6df31565c1c42793d5';
                console.log('Looking for topic:', entityAddedTopic);
                console.log('Log topic[0]:', log.topics[0]);
                if (log.topics[0] === entityAddedTopic) {
                  // The entity address is in the first indexed parameter (topics[1])
                  if (log.topics[1]) {
                    entityAddress = log.topics[1];
                    console.log('Found entity address from event:', entityAddress);
                    break;
                  }
                }
              }
            } catch (err) {
              console.warn('Failed to decode log:', err);
            }
          }
        }
        
        if (!entityAddress) {
          // Fallback: try to get the entity address by querying the contract
          // This assumes the entity address is the last created entity for this user
          setIpfsUploadProgress('Entity created! Querying for entity address...');
          
          try {
            // Get all entities from the contract
            const entities = await publicClient.readContract({
              address: ADDRESSES.TRINITY_MASTER as `0x${string}`,
              abi: CONFIG.TRINITY_ABI.abi as Abi,
              functionName: 'getEntities',
            });
            
            if (entities && Array.isArray(entities) && entities.length > 0) {
              // Get the last entity in the list (most recently created)
              entityAddress = entities[entities.length - 1];
              console.log('Found entity address from contract query:', entityAddress);
            }
          } catch (err) {
            console.warn('Failed to query contract for entity address:', err);
          }
          
          // If still no entity address, we'll proceed without it
          if (!entityAddress) {
            console.warn('Could not extract entity address from transaction receipt or contract query');
          }
        }
        
        // Step 2: Upload entity page to IPFS using the centralized template system
        setIpfsUploadProgress('Creating UI bundle...');
        
        try {
          // Generate entity page data with simplified interface
          const entityPageData = {
            entityAddress: entityAddress || '',
            entityName: name,
            entityDescription: description,
            entityType: selectedType
          };
          
          console.log('=== ENTITY PAGE DATA ===');
          console.log('Entity Address:', entityPageData.entityAddress);
          console.log('Entity Name:', entityPageData.entityName);
          console.log('Entity Description:', entityPageData.entityDescription);
          console.log('Entity Type:', entityPageData.entityType);
          console.log('========================');
          
          // Generate the single HTML file
          const files = generateEntityPageFiles(entityPageData);
          
          console.log('Generated UI files:', files);
          
          // Upload files as directory to IPFS via backend
          const uploadResult = await uploadDirectoryToBackend(files);
          
          const uploadedCID = uploadResult.cid;
          setIpfsCID(uploadedCID);
          setIpfsUploadProgress('Entity page uploaded to IPFS!');
          
          console.log('Entity page uploaded to IPFS:', uploadResult);
          
          // Step 3: Set UI page 0 with the uploaded CID
          if (entityAddress) {
            setIpfsUploadProgress('Setting UI page...');
            
            try {
              // Get the entity's properties contract address
              const propertiesAddress = await publicClient.readContract({
                address: entityAddress as `0x${string}`,
                abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
                functionName: 'properties',
              });
              
              console.log('Entity properties address:', propertiesAddress);
              
              // Call setUIpage on the properties contract
              const { request } = await publicClient.simulateContract({
                address: propertiesAddress as `0x${string}`,
                abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
                functionName: 'setUIpage',
                args: [0, uploadedCID],
                account: address!,
              });
              
              if (!walletClient) {
                throw new Error('Wallet client not available');
              }
              
              const hash = await walletClient.writeContract(request);
              console.log('setUIpage transaction hash:', hash);
              
              // Wait for the transaction to be confirmed
              const receipt = await publicClient.waitForTransactionReceipt({ hash });
              console.log('setUIpage transaction confirmed:', receipt);
              
              setIpfsUploadProgress(`Entity created successfully! UI page set to IPFS: ${uploadedCID}`);
              console.log('Entity address:', entityAddress);
              console.log('IPFS CID:', uploadedCID);
              
              // Call onSuccess callback
              onSuccess?.();
              
            } catch (err) {
              console.error('Failed to set UI page:', err);
              setError('Entity created and page uploaded, but failed to set UI page: ' + (err instanceof Error ? err.message : 'Unknown error'));
              setIpfsUploadProgress(`Entity created successfully! UI page uploaded to IPFS: ${uploadedCID} (Please set UI page 0 manually)`);
            }
          } else {
            setIpfsUploadProgress(`Entity created successfully! UI page uploaded to IPFS: ${uploadedCID}`);
            console.log('IPFS CID:', uploadedCID);
            console.log('Entity address not found. Please set UI page 0 manually to:', uploadedCID);
          }
          
        } catch (err) {
          console.error('Failed to create UI bundle:', err);
          setError('Entity created but failed to create UI bundle: ' + (err instanceof Error ? err.message : 'Unknown error'));
          setIpfsUploadProgress('');
        }
        
      } catch (err) {
        console.error('Failed to process entity creation:', err);
        setError('Entity created but failed to upload page: ' + (err instanceof Error ? err.message : 'Unknown error'));
        setIpfsUploadProgress('');
      } finally {
        setIsUploadingToIPFS(false);
      }
    },
    onError: (err) => {
      setError('Transaction failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setIpfsUploadProgress('');
    },
  });

  // Add a ref to store the simulated new token address
  const simulatedTokenAddressRef = useRef<string | null>(null);

  // When preparing to launch the token, simulate the contract call first
  const [isSimulatingToken, setIsSimulatingToken] = useState(false);

  const handleLaunchToken = async () => {
    setIsSimulatingToken(true);
    setTokenError(null);
    try {
      // Simulate the contract call to get the new token address
      const simulation = await publicClient.simulateContract({
        address: ADDRESSES.NT,
        abi: NT_ABI.abi as Abi,
        functionName: 'New',
        args: [tokenName, tokenSymbol, BigInt(initialMint) * 10n ** 18n, parentToken],
        account: address,
      });
      const newTokenAddress = simulation.result as string;
      simulatedTokenAddressRef.current = newTokenAddress;
      console.log('Simulated new token address:', newTokenAddress);
      // Now send the transaction
      launchToken?.();
    } catch (err) {
      setTokenError('Simulation failed: ' + (err as Error).message);
      setIsSimulatingToken(false);
    }
  };

  // Prepare NT contract write
  const { config: launchTokenConfig, error: launchTokenPrepareError } = usePrepareContractWrite({
    address: ADDRESSES.NT,
    abi: NT_ABI.abi as Abi,
    functionName: 'New',
    args: [tokenName, tokenSymbol, BigInt(initialMint) * 10n ** 18n, parentToken],
    enabled: showTokenModal && !!tokenName && !!tokenSymbol && !!initialMint && !!parentToken && !!ADDRESSES.NT,
    // TODO: Validate ntAddress is a valid 0x address
  });

  const { write: launchToken, isLoading: isLaunchingToken } = useContractWrite({
    ...launchTokenConfig,
    onSuccess: (data) => {
      setTokenTxHash(data.hash);
      setTokenError(null);
    },
    onError: (error) => {
      // Handle the "Fuck Off" error specifically
      if (error.message.includes('0xe450d38c') || error.message.includes('FuckOff')) {
        setTokenError('Permission denied: You do not have the required permissions to launch this token. This could be due to insufficient parent token ownership or other access restrictions.');
      } else {
        setTokenError(error.message);
      }
    },
  });

  useWaitForTransaction({
    hash: tokenTxHash,
    onSuccess: async (receipt) => {
      try {
        const newTokenAddress = simulatedTokenAddressRef.current;
        if (newTokenAddress) {
          setEntityToken(newTokenAddress);
          setTokenError(null);
          setShowTokenModal(false);
          alert(`Token launched successfully! New token address: ${newTokenAddress}`);
        } else {
          setTokenError('Token launched but could not retrieve the new token address. Please check the transaction receipt manually.');
          setShowTokenModal(false);
        }
      } catch (error) {
        setTokenError('Token launched but could not retrieve the new token address. Please check the transaction receipt manually.');
        setShowTokenModal(false);
      }
    },
    onError: () => setTokenError('Token launch failed.'),
  });

  // Add debugging for entityToken changes
  useEffect(() => {
    console.log('Entity token updated:', entityToken);
  }, [entityToken]);

  // Add state for entity token approval
  const [needsEntityTokenApproval, setNeedsEntityTokenApproval] = useState(false);
  const [entityTokenApprovalTxHash, setEntityTokenApprovalTxHash] = useState<`0x${string}` | undefined>();

  // Check if entity token approval is needed
  const { data: entityTokenAllowance } = useContractRead({
    address: entityToken as `0x${string}`,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'allowance',
    args: [address!, ADDRESSES.TRINITY_MASTER],
    enabled: !!address && !!entityToken && entityToken !== '0x0000000000000000000000000000000000000000',
  });

  // Get the required fee amount
  const { data: trinityEntityFee } = useContractRead({
    address: ADDRESSES.TRINITY_MASTER,
    abi: CONFIG.TRINITY_ABI.abi as Abi,
    functionName: 'trinityEntityFee',
    enabled: !!ADDRESSES.TRINITY_MASTER,
  });

  // Check if approval is needed
  useEffect(() => {
    if (entityTokenAllowance !== undefined && trinityEntityFee !== undefined) {
      setNeedsEntityTokenApproval((entityTokenAllowance as bigint) < (trinityEntityFee as bigint));
    }
  }, [entityTokenAllowance, trinityEntityFee]);

  // Prepare entity token approval transaction
  const { config: entityTokenApprovalConfig } = usePrepareContractWrite({
    address: entityToken as `0x${string}`,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'approve',
    args: [ADDRESSES.TRINITY_MASTER, trinityEntityFee || 0n],
    enabled: needsEntityTokenApproval && !!address && !!entityToken && trinityEntityFee !== undefined,
  });

  const { write: approveEntityToken, isLoading: isApprovingEntityToken } = useContractWrite({
    ...entityTokenApprovalConfig,
    onSuccess: (data) => {
      setEntityTokenApprovalTxHash(data.hash);
      setError(null);
    },
    onError: (error) => {
      setError(`Entity token approval failed: ${error.message}`);
    },
  });

  // Watch entity token approval transaction
  const { isLoading: isEntityTokenApprovalConfirming } = useWaitForTransaction({
    hash: entityTokenApprovalTxHash,
    onSuccess: () => {
      setNeedsEntityTokenApproval(false);
      setEntityTokenApprovalTxHash(undefined);
    },
    onError: () => {
      setError('Entity token approval transaction failed');
      setEntityTokenApprovalTxHash(undefined);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (deployEntity) {
        deployEntity();
    }
  };

  const getButtonText = () => {
    if (isUploadingToIPFS) return 'Uploading to IPFS...';
    if (isSubmitting) return 'Creating Entity...';
    if (isConfirming) return 'Confirming Transaction...';
    if (isApprovingEntityToken || isEntityTokenApprovalConfirming) return 'Approving Fee...';
    if (!address) return 'Connect Wallet First';
    if (!name || !description || !selectedType) return 'Fill All Fields';
    if (!ADDRESSES.TRINITY_MASTER) return 'Contract Not Found';
    if (!entityToken || entityToken === '0x0000000000000000000000000000000000000000') return 'Set Entity Token';
    if (needsEntityTokenApproval) return 'Approve Fee First';
    if (isBackendReady === false) return 'Backend Not Available';
    if (isBackendReady === null) return 'Checking Backend...';
    if (prepareError) return 'Invalid Parameters';
    return 'Create Entity & Upload Page';
  };

  return (
    <div className="glass-panel p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary">Create New Entity</h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {isSubmitting && !error && (
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-blue-500 text-sm">
            {isSubmitting ? 'Please confirm the transaction in your wallet...' : 'Transaction submitted, waiting for confirmation...'}
          </p>
        </div>
      )}

      {ipfsUploadProgress && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-green-500 text-sm">{ipfsUploadProgress}</p>
          {ipfsCID && (
            <p className="text-green-400 text-xs mt-1">
              IPFS CID: {ipfsCID}
            </p>
          )}
        </div>
      )}

      {/* Backend Status Indicator */}
      <div className="mb-4 p-3 rounded-lg text-sm border">
        {isBackendReady === null && (
          <div className="flex items-center gap-2 text-yellow-500">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            Testing backend connection...
          </div>
        )}
        {isBackendReady === true && (
          <div className="flex items-center gap-2 text-green-500">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            ✓ Backend ready for uploads
          </div>
        )}
        {isBackendReady === false && (
          <div className="flex items-center gap-2 text-red-500">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            ✗ Backend connection failed
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Entity Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            placeholder="Enter entity name"
            required
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 h-24"
            placeholder="Enter entity description"
            required
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Entity Type
          </label>
          <select
            className="input-field w-full"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as EntityType)}
          >
            {ENTITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Entity Token
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="input-field w-full bg-gray-700"
              value={entityToken}
              key={entityToken}
              onChange={(e) => setEntityToken(e.target.value)}
              placeholder="Enter token address or use Launch Token button"
            />
            <button
              type="button"
              className="px-2 py-1 bg-green-700/80 rounded text-xs hover:bg-green-700"
              onClick={() => setShowTokenModal(true)}
              disabled={isSubmitting}
            >
              Launch Token
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">TRINITY Token Address (Auto-filled or custom)</p>
        </div>

        {/* Entity Token Approval */}
        {needsEntityTokenApproval && (
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-lg font-medium text-primary mb-3">Token Approval Required</h3>
            <button
              type="button"
              onClick={() => approveEntityToken?.()}
              disabled={isApprovingEntityToken || isEntityTokenApprovalConfirming}
              className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isApprovingEntityToken || isEntityTokenApprovalConfirming 
                ? 'Approving...' 
                : `Approve ${trinityEntityFee ? Number(trinityEntityFee) / 1e18 : 0} Tokens`
              }
            </button>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || isUploadingToIPFS || needsEntityTokenApproval || isBackendReady === false || !name || !description}
          className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {getButtonText()}
        </button>
      </form>

      {/* Token Launch Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-panel p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary">Launch New Token</h3>
              <button
                onClick={() => setShowTokenModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            {tokenError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-500 text-sm">{tokenError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Token Name</label>
                <input
                  type="text"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="e.g., My Token"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Token Symbol</label>
                <input
                  type="text"
                  value={tokenSymbol}
                  onChange={(e) => setTokenSymbol(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="e.g., MTK"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Initial Supply</label>
                <input
                  type="text"
                  value={initialMint}
                  onChange={(e) => setInitialMint(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="e.g., 1000000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Parent Token</label>
                <input
                  type="text"
                  value={parentToken}
                  onChange={(e) => setParentToken(e.target.value as `0x${string}`)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="Parent token address"
                />
              </div>

              {needsApproval && (
                <button
                  type="button"
                  onClick={() => approve?.()}
                  disabled={isApproving || isApprovalConfirming}
                  className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  {isApproving || isApprovalConfirming ? 'Approving...' : 'Approve Parent Token'}
                </button>
              )}

              <button
                type="button"
                onClick={handleLaunchToken}
                disabled={!tokenName || !tokenSymbol || !initialMint || isSimulatingToken || isLaunchingToken}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {isSimulatingToken || isLaunchingToken ? 'Launching Token...' : 'Launch Token'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 